import { useMemo, useState, type FormEvent } from "react";
import QuoteSummary from "./QuoteSummary";

const FORM_URL = "https://readdy.ai/api/form/d8ekokdpg3g3be9asn90";

type MoveType = "residential" | "commercial" | "long-distance" | "packing" | "storage" | "specialty";

const MOVE_TYPES: { value: MoveType; label: string; icon: string }[] = [
  { value: "residential", label: "Residential", icon: "ri-home-smile-line" },
  { value: "commercial", label: "Commercial", icon: "ri-building-2-line" },
  { value: "long-distance", label: "Long Distance", icon: "ri-roadster-line" },
  { value: "packing", label: "Packing Only", icon: "ri-archive-2-line" },
  { value: "storage", label: "Storage", icon: "ri-inbox-archive-line" },
  { value: "specialty", label: "Specialty", icon: "ri-music-2-line" },
];

type HomeSize = {
  key: string;
  label: string;
  basePrice: number;
  hours: number;
  crew: number;
  truck: string;
  cuft: number;
};

const HOME_SIZES: HomeSize[] = [
  { key: "studio", label: "Studio", basePrice: 480, hours: 3, crew: 2, truck: "16 ft", cuft: 350 },
  { key: "1br", label: "1 Bedroom", basePrice: 680, hours: 4, crew: 2, truck: "20 ft", cuft: 600 },
  { key: "2br", label: "2 Bedroom", basePrice: 980, hours: 5, crew: 3, truck: "20 ft", cuft: 900 },
  { key: "3br", label: "3 Bedroom", basePrice: 1480, hours: 7, crew: 3, truck: "26 ft", cuft: 1400 },
  { key: "4br", label: "4+ Bedroom", basePrice: 2180, hours: 9, crew: 4, truck: "26 ft", cuft: 2000 },
  { key: "office", label: "Office", basePrice: 1880, hours: 6, crew: 4, truck: "26 ft", cuft: 1800 },
];

type AddOn = {
  key: string;
  label: string;
  price: number;
  icon: string;
  meta: string;
};

const ADD_ONS: AddOn[] = [
  { key: "packing", label: "Full packing", price: 380, icon: "ri-archive-2-line", meta: "Materials + 2 packers" },
  { key: "unpacking", label: "Unpacking", price: 220, icon: "ri-inbox-unarchive-line", meta: "We empty boxes for you" },
  { key: "storage", label: "1 month storage", price: 180, icon: "ri-inbox-archive-line", meta: "Climate-controlled vault" },
  { key: "piano", label: "Piano / specialty", price: 320, icon: "ri-music-2-line", meta: "Specialty crew + rigging" },
  { key: "disassembly", label: "Furniture disassembly", price: 95, icon: "ri-tools-line", meta: "Beds, desks, cribs" },
  { key: "insurance", label: "Full-value upgrade", price: 145, icon: "ri-shield-star-line", meta: "$10k+ items covered" },
];

const STAIRS_PER_FLIGHT = 65;
const LONG_HAUL_PER_MILE = 1.85;
const TAX_RATE = 0.08875;

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  // Step 1
  const [moveType, setMoveType] = useState<MoveType>("residential");
  const [homeSize, setHomeSize] = useState<string>("1br");
  const [stairsFrom, setStairsFrom] = useState(0);
  const [stairsTo, setStairsTo] = useState(0);
  const [distance, setDistance] = useState(8);

  // Step 2
  const [addons, setAddons] = useState<string[]>([]);

  // Step 3
  const [moveDate, setMoveDate] = useState("");
  const [messageLen, setMessageLen] = useState(0);

  const selectedHome = HOME_SIZES.find((h) => h.key === homeSize) ?? HOME_SIZES[1];
  const isLongDistance = moveType === "long-distance" || distance > 50;

  const lineItems = useMemo(() => {
    const items: { label: string; amount: number; meta?: string }[] = [];

    // Base
    const moveTypeLabel = MOVE_TYPES.find((m) => m.value === moveType)?.label ?? "";
    items.push({
      label: `${selectedHome.label} ${moveTypeLabel.toLowerCase()} move`,
      amount: selectedHome.basePrice,
      meta: `${selectedHome.crew}-person crew · ${selectedHome.hours}h estimate · ${selectedHome.truck} truck`,
    });

    // Distance
    if (isLongDistance) {
      const miles = Math.max(distance, 51);
      const longHaul = Math.round(miles * LONG_HAUL_PER_MILE);
      items.push({
        label: "Long-distance haul",
        amount: longHaul,
        meta: `${miles} mi × $${LONG_HAUL_PER_MILE.toFixed(2)}/mi · dedicated truck`,
      });
    } else if (distance > 15) {
      const extra = Math.round((distance - 15) * 12);
      items.push({
        label: "Extended local mileage",
        amount: extra,
        meta: `${distance - 15} mi beyond included radius`,
      });
    }

    // Stairs
    const totalFlights = stairsFrom + stairsTo;
    if (totalFlights > 2) {
      const billable = totalFlights - 2;
      items.push({
        label: `${billable} extra flight${billable > 1 ? "s" : ""} of stairs`,
        amount: billable * STAIRS_PER_FLIGHT,
        meta: "Beyond 2 flights included free",
      });
    }

    // Add-ons
    addons.forEach((key) => {
      const a = ADD_ONS.find((x) => x.key === key);
      if (a) items.push({ label: a.label, amount: a.price, meta: a.meta });
    });

    // Discount for early bird booking
    if (moveDate) {
      const days = Math.floor((new Date(moveDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      if (days >= 21) {
        items.push({
          label: "Early-bird discount",
          amount: -75,
          meta: "Booked 3+ weeks ahead",
        });
      }
    }

    return items;
  }, [moveType, selectedHome, isLongDistance, distance, stairsFrom, stairsTo, addons, moveDate]);

  const subtotal = useMemo(() => lineItems.reduce((sum, i) => sum + i.amount, 0), [lineItems]);
  const tax = Math.round(subtotal * TAX_RATE);
  const total = subtotal + tax;
  const deposit = Math.max(150, Math.round(total * 0.15 / 10) * 10);

  function toggleAddon(key: string) {
    setAddons((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    if ((formData.get("message")?.toString() || "").length > 500) {
      setErrorMsg("Message must be 500 characters or less");
      setStatus("error");
      return;
    }

    setStatus("submitting");
    setErrorMsg("");

    // Append computed quote
    formData.append("estimateTotal", `$${total}`);
    formData.append("estimateDeposit", `$${deposit}`);
    formData.append("estimateCrew", String(selectedHome.crew));
    formData.append("estimateHours", String(selectedHome.hours));
    formData.append("estimateTruck", selectedHome.truck);
    formData.append("addonsList", addons.join(", "));

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, value.toString());
      });

      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      if (!res.ok) throw new Error("Submission failed");
      setStatus("success");
      form.reset();
      setMoveType("residential");
      setHomeSize("1br");
      setStairsFrom(0);
      setStairsTo(0);
      setDistance(8);
      setAddons([]);
      setMoveDate("");
      setMessageLen(0);
    } catch {
      setStatus("error");
      setErrorMsg("Something went wrong sending your request. Please call us or try again.");
    }
  }

  if (status === "success") {
    return (
      <div className="bg-white rounded-2xl border border-ink-100 p-8 md:p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
          <i className="ri-check-line text-3xl"></i>
        </div>
        <h3 className="font-display text-2xl md:text-3xl font-bold text-ink-900 mb-3">
          Quote locked in — we'll be in touch!
        </h3>
        <p className="text-sm md:text-base text-ink-600 leading-relaxed max-w-md mx-auto mb-6">
          Thanks for the details. A real human from our team will reply with a confirmed flat-rate quote — usually within the hour during business hours.
        </p>
        <button
          onClick={() => setStatus("idle")}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer whitespace-nowrap transition-colors"
        >
          Calculate another move
        </button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
      {/* Calculator form */}
      <div className="lg:col-span-7 xl:col-span-8">
        <form
          id="haulmark-quote-calculator"
          data-readdy-form
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl border border-ink-100 overflow-hidden"
        >
          {/* Header banner */}
          <div className="px-6 md:px-8 py-5 border-b border-ink-100 flex items-center justify-between gap-4 bg-gradient-to-r from-cream-50 to-white">
            <div>
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-brand-100 text-brand-700 text-[10px] font-bold uppercase tracking-wider mb-2">
                <i className="ri-calculator-line"></i>
                Live quote calculator
              </div>
              <h2 className="font-display text-xl md:text-2xl font-bold text-ink-900">
                Build your move, see your price.
              </h2>
            </div>
            <div className="hidden md:flex items-center gap-2 text-xs text-ink-500">
              <i className="ri-flashlight-line text-brand-500"></i>
              <span>Updates as you click</span>
            </div>
          </div>

          {/* STEP 1 — MOVE DETAILS */}
          <section className="px-6 md:px-8 py-7 border-b border-ink-100">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-ink-900 text-white text-xs font-bold">
                1
              </div>
              <h3 className="font-display text-lg font-bold text-ink-900">Tell us about your move</h3>
            </div>

            <div className="space-y-6">
              {/* Move type */}
              <div>
                <label className="block text-xs uppercase tracking-[0.12em] font-bold text-ink-700 mb-2.5">
                  Move type
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {MOVE_TYPES.map((t) => (
                    <label
                      key={t.value}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-md text-xs font-semibold cursor-pointer border transition-colors ${
                        moveType === t.value
                          ? "border-brand-500 bg-brand-50 text-brand-700"
                          : "border-ink-200 bg-white text-ink-600 hover:border-ink-400"
                      }`}
                    >
                      <input
                        type="radio"
                        name="moveType"
                        value={t.value}
                        checked={moveType === t.value}
                        onChange={() => setMoveType(t.value)}
                        className="sr-only"
                      />
                      <i className={`${t.icon} text-base`}></i>
                      <span className="truncate">{t.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Home size */}
              <div>
                <label className="block text-xs uppercase tracking-[0.12em] font-bold text-ink-700 mb-2.5">
                  Home / space size
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {HOME_SIZES.map((s) => (
                    <label
                      key={s.key}
                      className={`relative flex flex-col items-start px-4 py-3 rounded-md cursor-pointer border transition-colors ${
                        homeSize === s.key
                          ? "border-brand-500 bg-brand-50"
                          : "border-ink-200 bg-white hover:border-ink-400"
                      }`}
                    >
                      <input
                        type="radio"
                        name="homeSize"
                        value={s.label}
                        checked={homeSize === s.key}
                        onChange={() => setHomeSize(s.key)}
                        className="sr-only"
                      />
                      <span className={`text-sm font-bold ${homeSize === s.key ? "text-brand-700" : "text-ink-900"}`}>
                        {s.label}
                      </span>
                      <span className="text-[11px] text-ink-500 mt-0.5">
                        ~{s.cuft} cu ft · {s.crew} movers
                      </span>
                      {homeSize === s.key && (
                        <i className="ri-checkbox-circle-fill absolute top-2 right-2 text-brand-500 text-sm"></i>
                      )}
                    </label>
                  ))}
                </div>
              </div>

              {/* Distance slider */}
              <div>
                <div className="flex items-center justify-between mb-2.5">
                  <label htmlFor="distance" className="text-xs uppercase tracking-[0.12em] font-bold text-ink-700">
                    Approx. distance
                  </label>
                  <span className="text-sm font-display font-bold text-brand-600 tabular-nums">
                    {distance} {distance === 1 ? "mile" : "miles"}
                    {isLongDistance && <span className="ml-2 text-[10px] uppercase tracking-wider text-ink-400 font-bold">Long-distance</span>}
                  </span>
                </div>
                <input
                  id="distance"
                  name="distance"
                  type="range"
                  min={1}
                  max={500}
                  step={1}
                  value={distance}
                  onChange={(e) => setDistance(Number(e.target.value))}
                  className="w-full h-2 rounded-full bg-ink-100 appearance-none cursor-pointer accent-brand-500"
                />
                <div className="flex justify-between text-[10px] font-semibold text-ink-400 mt-1.5 uppercase tracking-wider">
                  <span>1 mi</span>
                  <span>50 mi · local</span>
                  <span>500 mi+</span>
                </div>
              </div>

              {/* Stairs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <StairCounter
                  label="Flights at pickup"
                  value={stairsFrom}
                  onChange={setStairsFrom}
                  name="stairsFrom"
                />
                <StairCounter
                  label="Flights at drop-off"
                  value={stairsTo}
                  onChange={setStairsTo}
                  name="stairsTo"
                />
              </div>

              {/* Addresses */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="fromAddress" className="block text-xs uppercase tracking-[0.12em] font-bold text-ink-700 mb-2">
                    Moving from
                  </label>
                  <div className="relative">
                    <span className="w-5 h-5 flex items-center justify-center absolute left-3.5 top-1/2 -translate-y-1/2 text-brand-500">
                      <i className="ri-map-pin-line text-sm"></i>
                    </span>
                    <input
                      id="fromAddress"
                      name="fromAddress"
                      type="text"
                      required
                      maxLength={200}
                      placeholder="ZIP or address"
                      className="w-full text-sm pl-10 pr-4 py-3 rounded-md bg-cream-50 border border-ink-200 focus:bg-white focus:border-brand-500 outline-none transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="toAddress" className="block text-xs uppercase tracking-[0.12em] font-bold text-ink-700 mb-2">
                    Moving to
                  </label>
                  <div className="relative">
                    <span className="w-5 h-5 flex items-center justify-center absolute left-3.5 top-1/2 -translate-y-1/2 text-brand-500">
                      <i className="ri-map-pin-2-line text-sm"></i>
                    </span>
                    <input
                      id="toAddress"
                      name="toAddress"
                      type="text"
                      required
                      maxLength={200}
                      placeholder="ZIP or address"
                      className="w-full text-sm pl-10 pr-4 py-3 rounded-md bg-cream-50 border border-ink-200 focus:bg-white focus:border-brand-500 outline-none transition-colors"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* STEP 2 — ADD-ONS */}
          <section className="px-6 md:px-8 py-7 border-b border-ink-100 bg-cream-50/40">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-ink-900 text-white text-xs font-bold">
                2
              </div>
              <h3 className="font-display text-lg font-bold text-ink-900">Add-ons (optional)</h3>
              {addons.length > 0 && (
                <span className="ml-auto text-[11px] font-semibold text-brand-600 bg-brand-100 px-2 py-1 rounded">
                  {addons.length} selected
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {ADD_ONS.map((a) => {
                const selected = addons.includes(a.key);
                return (
                  <button
                    type="button"
                    key={a.key}
                    onClick={() => toggleAddon(a.key)}
                    className={`group text-left flex items-center gap-3 p-3.5 rounded-md border transition-colors cursor-pointer ${
                      selected
                        ? "border-brand-500 bg-brand-50"
                        : "border-ink-200 bg-white hover:border-ink-400"
                    }`}
                  >
                    <div
                      className={`flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-md ${
                        selected ? "bg-brand-500 text-white" : "bg-cream-100 text-ink-700"
                      }`}
                    >
                      <i className={`${a.icon} text-lg`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-sm font-bold ${selected ? "text-brand-700" : "text-ink-900"}`}>
                          {a.label}
                        </span>
                        <span className={`text-sm font-display font-bold tabular-nums ${selected ? "text-brand-600" : "text-ink-500"}`}>
                          +${a.price}
                        </span>
                      </div>
                      <div className="text-[11px] text-ink-500 truncate">{a.meta}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            {/* hidden field for submitted addons */}
            <input type="hidden" name="addons" value={addons.join(", ")} />
          </section>

          {/* STEP 3 — CONTACT */}
          <section className="px-6 md:px-8 py-7">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-ink-900 text-white text-xs font-bold">
                3
              </div>
              <h3 className="font-display text-lg font-bold text-ink-900">Where do we send your quote?</h3>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-xs uppercase tracking-[0.12em] font-bold text-ink-700 mb-2">
                    First name
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    maxLength={60}
                    placeholder="Jane"
                    className="w-full text-sm px-4 py-3 rounded-md bg-cream-50 border border-ink-200 focus:bg-white focus:border-brand-500 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-xs uppercase tracking-[0.12em] font-bold text-ink-700 mb-2">
                    Last name
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    maxLength={60}
                    placeholder="Doe"
                    className="w-full text-sm px-4 py-3 rounded-md bg-cream-50 border border-ink-200 focus:bg-white focus:border-brand-500 outline-none transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className="block text-xs uppercase tracking-[0.12em] font-bold text-ink-700 mb-2">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    maxLength={120}
                    placeholder="jane@example.com"
                    className="w-full text-sm px-4 py-3 rounded-md bg-cream-50 border border-ink-200 focus:bg-white focus:border-brand-500 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-xs uppercase tracking-[0.12em] font-bold text-ink-700 mb-2">
                    Phone
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    maxLength={20}
                    placeholder="(347) 555-0123"
                    className="w-full text-sm px-4 py-3 rounded-md bg-cream-50 border border-ink-200 focus:bg-white focus:border-brand-500 outline-none transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="moveDate" className="block text-xs uppercase tracking-[0.12em] font-bold text-ink-700 mb-2">
                  Preferred move date
                  <span className="ml-2 text-[10px] font-normal normal-case tracking-normal text-emerald-600">
                    Book 3+ weeks ahead, save $75
                  </span>
                </label>
                <input
                  id="moveDate"
                  name="moveDate"
                  type="date"
                  required
                  value={moveDate}
                  onChange={(e) => setMoveDate(e.target.value)}
                  className="w-full sm:max-w-xs text-sm px-4 py-3 rounded-md bg-cream-50 border border-ink-200 focus:bg-white focus:border-brand-500 outline-none transition-colors"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="message" className="text-xs uppercase tracking-[0.12em] font-bold text-ink-700">
                    Anything else we should know?
                  </label>
                  <span className={`text-[11px] ${messageLen > 500 ? "text-rose-600" : "text-ink-400"}`}>
                    {messageLen} / 500
                  </span>
                </div>
                <textarea
                  id="message"
                  name="message"
                  rows={3}
                  maxLength={500}
                  placeholder="Elevator reservations? Fragile items? Tell us what makes your move unique."
                  onChange={(e) => setMessageLen(e.target.value.length)}
                  className="w-full text-sm px-4 py-3 rounded-md bg-cream-50 border border-ink-200 focus:bg-white focus:border-brand-500 outline-none transition-colors resize-none"
                />
              </div>

              <label className="flex items-start gap-3 text-xs text-ink-600 leading-relaxed cursor-pointer">
                <input type="checkbox" name="consent" value="yes" required className="mt-0.5 accent-brand-500" />
                <span>
                  I'm okay with Haulmark contacting me about my quote by email, phone, or text. We don't share contact info — ever.
                </span>
              </label>

              {status === "error" && errorMsg && (
                <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-4 py-3 flex items-start gap-2">
                  <i className="ri-error-warning-line mt-0.5"></i>
                  <span>{errorMsg}</span>
                </div>
              )}

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md text-sm font-semibold bg-brand-500 hover:bg-brand-600 disabled:bg-brand-300 text-white cursor-pointer whitespace-nowrap transition-colors disabled:cursor-not-allowed"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Sending…
                    </>
                  ) : (
                    <>
                      Lock in this quote · ${total.toLocaleString()}
                      <i className="ri-arrow-right-line"></i>
                    </>
                  )}
                </button>
                <a
                  href="tel:+13475550142"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md text-sm font-semibold bg-cream-100 hover:bg-cream-200 text-ink-900 cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-phone-line"></i>
                  Call instead
                </a>
              </div>
            </div>
          </section>
        </form>
      </div>

      {/* Live summary */}
      <div className="lg:col-span-5 xl:col-span-4">
        <QuoteSummary
          lineItems={lineItems}
          subtotal={subtotal}
          tax={tax}
          total={total}
          deposit={deposit}
          crewSize={selectedHome.crew}
          estimatedHours={selectedHome.hours}
          truckSize={selectedHome.truck}
          isLongDistance={isLongDistance}
          status={status}
        />
      </div>
    </div>
  );
}

function StairCounter({
  label,
  value,
  onChange,
  name,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  name: string;
}) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.12em] font-bold text-ink-700 mb-2">
        {label}
        {value > 1 && (
          <span className="ml-2 text-[10px] font-normal normal-case tracking-normal text-ink-400">
            (2 flights total are free)
          </span>
        )}
      </label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onChange(Math.max(0, value - 1))}
          disabled={value === 0}
          className="w-11 h-11 flex items-center justify-center rounded-md border border-ink-200 hover:bg-cream-100 disabled:opacity-40 disabled:cursor-not-allowed text-ink-700 cursor-pointer"
        >
          <i className="ri-subtract-line"></i>
        </button>
        <div className="flex-1 text-center bg-cream-50 border border-ink-200 rounded-md py-3 font-display text-lg font-bold text-ink-900 tabular-nums">
          {value}
        </div>
        <button
          type="button"
          onClick={() => onChange(Math.min(8, value + 1))}
          className="w-11 h-11 flex items-center justify-center rounded-md border border-ink-200 hover:bg-cream-100 text-ink-700 cursor-pointer"
        >
          <i className="ri-add-line"></i>
        </button>
        <input type="hidden" name={name} value={value} />
      </div>
    </div>
  );
}